import { useApiQuery } from "@gladis/api-client";
import type {
  CurrentUserDto,
  DashboardDto,
  DashboardHealthDto,
  HealthPeriod,
  MemberDto,
} from "@gladis/shared";

/**
 * The page read.
 *
 * ==========================================================================================
 * THE SECOND `refetchOnMount` IN THE APPLICATION, AND THE STRONGER CASE FOR IT
 * ==========================================================================================
 *
 * The global default is a 30-second `staleTime`, and two modules decline to override it in the same
 * words — income's and investments' hooks both say "NO `refetchOnMount`. That exemption belongs to
 * the Expenses page, whose payload contains mortgage interest…".
 *
 * Read carefully, those comments name a CONDITION rather than a page: a payload holding figures the
 * page does not own and cannot observe changing. The dashboard satisfies it for EVERY figure it
 * holds rather than for one section of one payload — savings, investments, properties, mortgages,
 * expenses, income and the activity log all belong to other modules, and nothing this page can
 * observe tells it when one of them moved. A member who updates a balance and navigates here would
 * otherwise read a net worth that is thirty seconds stale in the one place the household goes to
 * find out where it stands.
 *
 * So this is not a second exception argued for by analogy. It is the case those two comments
 * described, arriving.
 */
export function useDashboard() {
  return useApiQuery<DashboardDto>(["dashboard"], "/dashboard", {
    staleTime: 0,
    refetchOnMount: "always",
  });
}

/**
 * The health card's own read — a SECOND query, and that is the whole of AC-D24.
 *
 * ==========================================================================================
 * THE FILTERS ARE IN THE KEY, WHICH IS WHY THE PAGE DOES NOT REDRAW
 * ==========================================================================================
 *
 * Changing the scope changes this key and nothing else, so React Query fetches this card and leaves
 * every other figure on the page — the net worth card, both charts — untouched, from cache, unmoved.
 * A single page read would have re-run a thirteen-month net-worth reconstruction to redraw three
 * numbers (FR-003, FR-004).
 *
 * The filters are in the key rather than only in the URL for the second half of the same rule: a
 * previous scope's response must never be applied to the current one. The key separates the two
 * cache entries and the payload ECHOES both values, so the card can tell whether what it holds is an
 * answer to what it is asking.
 */
export function useDashboardHealth(scope: string, period: HealthPeriod) {
  return useApiQuery<DashboardHealthDto>(
    ["dashboard", "health", scope, period],
    `/dashboard/health?scope=${encodeURIComponent(scope)}&period=${period}`,
    { staleTime: 0, refetchOnMount: "always" },
  );
}

/**
 * The household's current active members — the scope filter's option list.
 *
 * FETCHED FOR THE PICKER ONLY, and the key is deliberately the one four other pages already use, so
 * a member arriving from Expenses or Income pays no second request. The server resolves the scope
 * against identity's own answer and refuses anything else (FR-042); this copy exists so the control
 * can OFFER the right names, which is the difference between a convenience and a source of truth.
 */
export function useHouseholdMembers() {
  // Served from the cache `_authenticated` already populated — no extra request.
  const me = useApiQuery<CurrentUserDto>(["me"], "/me");
  const householdId = me.data?.activeHousehold?.id;

  return useApiQuery<{ members: MemberDto[] }>(
    ["members", householdId],
    `/households/${householdId}/members`,
    { enabled: Boolean(householdId) },
  );
}
